/**
 * FlagGate — conditionally renders children based on feature flag state.
 *
 * While flags are loading, renders a spinner (not a redirect) to prevent
 * flash-redirect on page load. Once loaded, evaluates the flag and either
 * renders children or the fallback.
 *
 * @module components/FlagGate
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFlag, useFlagLoaded } from '../contexts/FlagContext';
import { Spinner } from './Spinner';

interface FlagGateProps {
  /** Flag key to check (e.g. "commercial.portfolio_analytics") */
  flag: string;
  /** What to render when the flag is disabled. Defaults to redirect to /dashboard. */
  fallback?: ReactNode;
  children: ReactNode;
}

export function FlagGate({ flag, fallback, children }: FlagGateProps) {
  const loaded = useFlagLoaded();
  const enabled = useFlag(flag);

  // While flags are loading, show spinner — not a redirect.
  // This prevents flash-redirect when the page loads before the config API responds.
  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  if (!enabled) {
    return <>{fallback ?? <Navigate to="/dashboard" replace />}</>;
  }

  return <>{children}</>;
}
